"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Sparkles,
  PhoneCall,
  AlertTriangle
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  projectTypes,
  platforms,
  features,
  integrations,
  aiComplexity,
  infrastructure,
  disclaimers,
  businessRules,
  analyticsEvents,
  ProjectTypeId,
  PlatformTypeId,
  FeatureTypeId,
  IntegrationTypeId,
  AiTypeId,
  InfrastructureTypeId
} from "@/data/estimatorConfig";
import { calculateEstimate, EstimateInput } from "@/lib/estimator";
import { getStoredAttribution } from "@/components/analytics/UTMTracker";


// Analytics abstraction helper
const trackEvent = (eventName: string, data?: Record<string, unknown>) => {
  console.log(`[Estimator Analytics] Event: ${eventName}`, data);
  // If window.gtag exists, we could forward events:
  // if (typeof window !== 'undefined' && (window as any).gtag) {
  //   (window as any).gtag('event', eventName, data);
  // }
};

export default function ProjectEstimator() {
  const [step, setStep] = React.useState<number>(1);

  // Estimator Selection State
  const [projectTypeId, setProjectTypeId] = React.useState<ProjectTypeId | null>(null);
  const [platformId, setPlatformId] = React.useState<PlatformTypeId | null>(null);
  const [featureIds, setFeatureIds] = React.useState<FeatureTypeId[]>([]);
  const [integrationIds, setIntegrationIds] = React.useState<IntegrationTypeId[]>([]);
  const [aiId, setAiId] = React.useState<AiTypeId | null>(null);
  const [infrastructureId, setInfrastructureId] = React.useState<InfrastructureTypeId | null>(null);

  // Lead Form State
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [notes, setNotes] = React.useState("");

  // API Status State
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Dual Currency Switcher (USD vs INR) with Auto-Detection
  const [currency, setCurrency] = React.useState<"USD" | "INR">("USD");

  React.useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && (tz.includes("Kolkata") || tz.includes("Calcutta") || tz.includes("India") || tz.includes("Asia/Colombo"))) {
        setCurrency("INR");
      } else {
        setCurrency("USD");
      }
    } catch {
      setCurrency("USD");
    }
  }, []);

  // Fire analytics when starting
  React.useEffect(() => {
    trackEvent(analyticsEvents.estimatorStarted);
  }, []);


  const handleNextStep = () => {
    trackEvent(analyticsEvents.estimatorStepCompleted, { step });
    setStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handleToggleFeature = (id: FeatureTypeId) => {
    setFeatureIds((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const handleToggleIntegration = (id: IntegrationTypeId) => {
    setIntegrationIds((prev) =>
      prev.includes(id) ? prev.filter((iid) => iid !== id) : [...prev, id]
    );
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setSubmitError("Please fill in your name and email address.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);


    // Calculate details for API payload representation
    const activeEstimateInput: EstimateInput = {
      projectTypeId: projectTypeId || projectTypes[0].id,
      platformId: platformId || platforms[0].id,
      featureIds,
      integrationIds,
      aiId: aiId || aiComplexity[0].id,
      infrastructureId: infrastructureId || infrastructure[0].id,
    };

    const res = calculateEstimate(activeEstimateInput, currency);

    const projectLabel = projectTypes.find((p) => p.id === projectTypeId)?.label || "";
    const platformLabel = platforms.find((p) => p.id === platformId)?.label || "";
    const aiLabel = aiComplexity.find((a) => a.id === aiId)?.label || "";
    const infraLabel = infrastructure.find((i) => i.id === infrastructureId)?.label || "";

    const selectedFeaturesText = features
      .filter((f) => featureIds.includes(f.id))
      .map((f) => `- ${f.label}`)
      .join("\n");

    const selectedIntegrationsText = integrations
      .filter((i) => integrationIds.includes(i.id))
      .map((i) => `- ${i.label}`)
      .join("\n");

    const structuredMessage = `
[B2B Project Scoping Calculator Submission]
Project Type: ${projectLabel} (${projectTypeId})
Platform: ${platformLabel} (${platformId})
Market Currency: ${currency}

Selected Features:
${selectedFeaturesText || "None"}

Selected Integrations:
${selectedIntegrationsText || "None"}

AI Operations: ${aiLabel} (${aiId})
Infrastructure: ${infraLabel} (${infrastructureId})

[Calculation Summary]
Total Points: ${res.totalPoints}
Complexity: ${res.complexity.label}
Estimated Timeline: ${res.complexity.timelineWeeks.min}–${res.complexity.timelineWeeks.max} weeks
Estimated Cost Range: ${res.formattedMin} – ${res.formattedMax} (${currency})

[Lead Contact Details]
Company: ${company || "Independent / Startup"}
Phone: ${phone || "Not provided"}
Project Notes: ${notes || "None provided"}
    `.trim();

    try {
      const attribution = getStoredAttribution();

      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          phone: phone || undefined,
          company: company || undefined,
          service_type: projectLabel || "Custom Software Development",
          budget_range: `${res.formattedMin} – ${res.formattedMax}`,
          currency,
          timeline: `${res.complexity.timelineWeeks.min}–${res.complexity.timelineWeeks.max} weeks`,
          project_details: structuredMessage,
          estimated_deal_value: res.minEstimate,
          landing_page: "/resources/project-estimator",
          ...attribution,
        }),
      });

      const data = await response.json();


      if (response.ok && data.success) {
        trackEvent(analyticsEvents.estimatorLeadSubmitted, {
          points: res.totalPoints,
          complexity: res.complexity.id,
          currency,
        });
        handleNextStep();
      } else {
        setSubmitError(data.error || "Failed to submit request. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setSubmitError("An unexpected network error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Compile calculations for step 8 display
  const activeEstimateInput: EstimateInput = {
    projectTypeId: projectTypeId || projectTypes[0].id,
    platformId: platformId || platforms[0].id,
    featureIds,
    integrationIds,
    aiId: aiId || aiComplexity[0].id,
    infrastructureId: infrastructureId || infrastructure[0].id,
  };
  const results = calculateEstimate(activeEstimateInput, currency);

  // Wizard Navigation Guard Checks
  const isStep1Valid = projectTypeId !== null;
  const isStep2Valid = platformId !== null;
  const isStep5Valid = aiId !== null;
  const isStep6Valid = infrastructureId !== null;

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Top Currency Switcher & Trust Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-md border border-border-muted/50 shadow-sm">
        <div className="flex items-center gap-2.5">
          <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <div>
            <span className="text-xs font-bold text-foreground block">
              Commercial Scoping Engine
            </span>
            <span className="text-[11px] text-text-muted">
              Select your market currency for calibrated planning estimates
            </span>
          </div>
        </div>

        <div className="inline-flex items-center p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-border-muted/60 text-xs font-bold shadow-inner">
          <button
            type="button"
            onClick={() => setCurrency("USD")}
            className={`px-3.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
              currency === "USD"
                ? "bg-white dark:bg-slate-900 text-primary shadow-sm border border-border-muted/40"
                : "text-text-muted hover:text-foreground"
            }`}
          >
            <span>🇺🇸</span>
            <span>USD ($)</span>
          </button>
          <button
            type="button"
            onClick={() => setCurrency("INR")}
            className={`px-3.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
              currency === "INR"
                ? "bg-white dark:bg-slate-900 text-primary shadow-sm border border-border-muted/40"
                : "text-text-muted hover:text-foreground"
            }`}
          >
            <span>🇮🇳</span>
            <span>INR (₹)</span>
          </button>
        </div>
      </div>

      {/* Progress Header */}
      {step < 8 && (
        <div className="space-y-2">
          <div className="flex justify-between items-center text-xs text-text-muted">
            <span>Step {step} of 7</span>
            <span>{Math.round((step / 7) * 100)}% Complete</span>
          </div>
          <div className="w-full h-1 bg-border-muted/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
              style={{ width: `${(step / 7) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="min-h-[400px] flex flex-col justify-between">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  Select your primary project type
                </h2>
                <p className="text-xs sm:text-sm text-text-muted">
                  Choose the classification that closest matches your development intent.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projectTypes.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setProjectTypeId(item.id);
                      handleNextStep();
                    }}
                    className={`text-left p-6 rounded-xl border transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer ${
                      projectTypeId === item.id
                        ? "bg-primary/5 border-primary shadow-sm"
                        : "bg-white dark:bg-slate-900 border-border-muted hover:border-text-muted/40"
                    }`}
                  >
                    <span className="block font-heading font-bold text-base text-foreground mb-1">
                      {item.label}
                    </span>
                    <span className="block text-xs text-text-muted leading-relaxed">
                      {item.description}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  Select targets deployment platforms
                </h2>
                <p className="text-xs sm:text-sm text-text-muted">
                  Specify whether users will access the application on web browsers, mobile apps, or both.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {platforms.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setPlatformId(item.id);
                      handleNextStep();
                    }}
                    className={`text-left p-6 rounded-xl border transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer ${
                      platformId === item.id
                        ? "bg-primary/5 border-primary shadow-sm"
                        : "bg-white dark:bg-slate-900 border-border-muted hover:border-text-muted/40"
                    }`}
                  >
                    <span className="block font-heading font-bold text-base text-foreground mb-1">
                      {item.label}
                    </span>
                    <span className="block text-xs text-text-muted leading-relaxed">
                      {item.description}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  Select system features (Select all that apply)
                </h2>
                <p className="text-xs sm:text-sm text-text-muted">
                  Choose the structural modules that need to be engineered.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {features.map((item) => {
                  const isChecked = featureIds.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleToggleFeature(item.id)}
                      className={`text-left p-6 rounded-xl border transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer flex items-start gap-4 ${
                        isChecked
                          ? "bg-primary/5 border-primary shadow-sm"
                          : "bg-white dark:bg-slate-900 border-border-muted hover:border-text-muted/40"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${
                          isChecked ? "bg-primary border-primary text-white" : "border-border-muted"
                        }`}
                      >
                        {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                      </div>
                      <div>
                        <span className="block font-heading font-bold text-base text-foreground mb-1">
                          {item.label}
                        </span>
                        <span className="block text-xs text-text-muted leading-relaxed">
                          {item.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  Select integrations (Select all that apply)
                </h2>
                <p className="text-xs sm:text-sm text-text-muted">
                  Choose the third-party platforms or databases your application needs to coordinate with.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {integrations.map((item) => {
                  const isChecked = integrationIds.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleToggleIntegration(item.id)}
                      className={`text-left p-6 rounded-xl border transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer flex items-start gap-3 ${
                        isChecked
                          ? "bg-primary/5 border-primary shadow-sm"
                          : "bg-white dark:bg-slate-900 border-border-muted hover:border-text-muted/40"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${
                          isChecked ? "bg-primary border-primary text-white" : "border-border-muted"
                        }`}
                      >
                        {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                      </div>
                      <div>
                        <span className="block font-heading font-bold text-base text-foreground mb-1">
                          {item.label}
                        </span>
                        <span className="block text-xs text-text-muted leading-relaxed">
                          {item.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  AI & Intelligent Operations
                </h2>
                <p className="text-xs sm:text-sm text-text-muted">
                  Determine if your build requires custom LLM integrations or RAG vector pipelines.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aiComplexity.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setAiId(item.id);
                      handleNextStep();
                    }}
                    className={`text-left p-6 rounded-xl border transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer ${
                      aiId === item.id
                        ? "bg-primary/5 border-primary shadow-sm"
                        : "bg-white dark:bg-slate-900 border-border-muted hover:border-text-muted/40"
                    }`}
                  >
                    <span className="block font-heading font-bold text-base text-foreground mb-1 flex items-center gap-2">
                      {item.id !== "none" && <Sparkles className="h-4 w-4 text-accent shrink-0" />}
                      {item.label}
                    </span>
                    <span className="block text-xs text-text-muted leading-relaxed">
                      {item.description}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 6 && (
            <motion.div
              key="step6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                  Hosting & DevOps Architecture
                </h2>
                <p className="text-xs sm:text-sm text-text-muted">
                  Choose the cloud hosting tier required for operational stability and security compliance.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {infrastructure.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setInfrastructureId(item.id);
                      handleNextStep();
                    }}
                    className={`text-left p-6 rounded-xl border transition-all duration-200 outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer ${
                      infrastructureId === item.id
                        ? "bg-primary/5 border-primary shadow-sm"
                        : "bg-white dark:bg-slate-900 border-border-muted hover:border-text-muted/40"
                    }`}
                  >
                    <span className="block font-heading font-bold text-base text-foreground mb-1">
                      {item.label}
                    </span>
                    <span className="block text-xs text-text-muted leading-relaxed">
                      {item.description}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 7 && (
            <motion.div
              key="step7"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8"
            >
              {/* Left Column: Review Selections */}
              <div className="lg:col-span-7 space-y-6">
                <div className="space-y-2">
                  <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground">
                    Confirm your scoping requirements
                  </h2>
                  <p className="text-xs sm:text-sm text-text-muted">
                    We require business contact details to finalize project timeline estimates.
                  </p>
                </div>

                <div className="p-6 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-border-muted/50 space-y-4 text-xs">
                  <h3 className="font-heading font-bold text-foreground uppercase tracking-wider text-[10px]">
                    Summary of Selections:
                  </h3>
                  <div className="grid grid-cols-2 gap-y-3 border-t border-border-muted/30 pt-3">
                    <div>
                      <span className="text-text-muted block">Project Type:</span>
                      <span className="font-bold text-foreground">
                        {projectTypes.find((p) => p.id === projectTypeId)?.label}
                      </span>
                    </div>
                    <div>
                      <span className="text-text-muted block">Platform:</span>
                      <span className="font-bold text-foreground">
                        {platforms.find((p) => p.id === platformId)?.label}
                      </span>
                    </div>
                    <div>
                      <span className="text-text-muted block">AI Model:</span>
                      <span className="font-bold text-foreground">
                        {aiComplexity.find((a) => a.id === aiId)?.label}
                      </span>
                    </div>
                    <div>
                      <span className="text-text-muted block">Hosting Infrastructure:</span>
                      <span className="font-bold text-foreground">
                        {infrastructure.find((i) => i.id === infrastructureId)?.label}
                      </span>
                    </div>
                  </div>

                  {(featureIds.length > 0 || integrationIds.length > 0) && (
                    <div className="border-t border-border-muted/30 pt-3 space-y-2">
                      {featureIds.length > 0 && (
                        <div>
                          <span className="text-text-muted block mb-1">Features:</span>
                          <span className="font-bold text-foreground block">
                            {features
                              .filter((f) => featureIds.includes(f.id))
                              .map((f) => f.label)
                              .join(", ")}
                          </span>
                        </div>
                      )}
                      {integrationIds.length > 0 && (
                        <div>
                          <span className="text-text-muted block mb-1">Integrations:</span>
                          <span className="font-bold text-foreground block">
                            {integrations
                              .filter((i) => integrationIds.includes(i.id))
                              .map((i) => i.label)
                              .join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Lead Form */}
              <Card className="lg:col-span-5 p-6 sm:p-8" hoverGlow={false}>
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-4">
                    Unlock Estimate Range
                  </h3>

                  {submitError && (
                    <div className="p-3 bg-error-muted/20 border border-error-muted text-error text-xs rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-error mt-0.5" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label htmlFor="name" className="block text-xs font-bold text-foreground">
                      Full Name *
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-border-muted rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="email" className="block text-xs font-bold text-foreground">
                      Business Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. john@company.com"
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-border-muted rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="company" className="block text-xs font-bold text-foreground">
                      Company / Startup Name (Optional)
                    </label>
                    <input
                      id="company"
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="e.g. Acme Corp (or leave blank if independent)"
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-border-muted rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>


                  <div className="space-y-1">
                    <label htmlFor="phone" className="block text-xs font-bold text-foreground">
                      Phone Number (Optional)
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g. +91 98765 43210"
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-border-muted rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="notes" className="block text-xs font-bold text-foreground">
                      Project Description (Optional)
                    </label>
                    <textarea
                      id="notes"
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Briefly tell us about your goals..."
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-border-muted rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="gradient"
                    className="w-full justify-center text-xs py-2 mt-4"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Calculating..." : "Reveal Budget Range"}
                    {!isSubmitting && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>

                  <p className="text-[9px] text-text-muted text-center leading-relaxed">
                    By submitting, you agree to our privacy policy. Your data is used exclusively to prepare this scoping analysis.
                  </p>
                </form>
              </Card>
            </motion.div>
          )}

          {step === 8 && (
            <motion.div
              key="step8"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8 text-center max-w-2xl mx-auto"
            >
              <div className="space-y-2">
                <span className="text-xs uppercase font-heading tracking-widest text-primary font-bold">
                  Calculation Complete
                </span>
                <h2 className="text-3xl font-heading font-extrabold text-foreground tracking-tight">
                  Your scoping estimate is ready
                </h2>
                <p className="text-xs text-text-muted max-w-lg mx-auto">
                  A summary report has been compiled and dispatched to <strong>{email}</strong>. Our engineering lead will contact you within 2 business hours.
                </p>
              </div>

              {/* Estimate Result Card */}
              <Card className="p-8 border-primary/20 bg-slate-50/50 dark:bg-slate-900/10" glowColor="rgba(37, 99, 235, 0.15)">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-border-muted/30">
                  <div className="flex flex-col justify-center items-center p-2">
                    <span className="text-text-muted text-xs mb-1 block">Project Complexity</span>
                    <span className="font-heading font-extrabold text-lg text-foreground">
                      {results.complexity.label}
                    </span>
                  </div>

                  <div className="flex flex-col justify-center items-center p-2">
                    <span className="text-text-muted text-xs mb-1 block">Estimated Timeline</span>
                    <span className="font-heading font-extrabold text-lg text-foreground">
                      {results.timelineWeeks.min}–{results.timelineWeeks.max} weeks
                    </span>
                  </div>

                  <div className="flex flex-col justify-center items-center p-2">
                    <span className="text-text-muted text-xs mb-1 block">Planning Budget Range</span>
                    <span className="font-heading font-extrabold text-xl text-primary dark:text-accent">
                      {results.formattedMin} – {results.formattedMax}
                    </span>
                  </div>

                </div>

                {/* Scope Drivers */}
                {results.selectedDrivers.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-border-muted/30 text-left">
                    <span className="block text-xs font-bold text-foreground mb-3">
                      Key drivers affecting this estimate:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {results.selectedDrivers.map((driver) => (
                        <span
                          key={driver}
                          className="px-2.5 py-1 bg-primary/5 text-primary dark:bg-primary/20 dark:text-accent rounded-full text-[10px] font-medium"
                        >
                          + {driver}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Disclaimer */}
              <p className="text-[10px] text-text-muted max-w-xl mx-auto leading-relaxed italic">
                {disclaimers.main}
              </p>

              {/* Lead Capture CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Button href="/contact" variant="gradient" size="lg" className="w-full sm:w-auto">
                  <PhoneCall className="mr-2 h-4 w-4" />
                  Book Scoping Consultation
                </Button>
                <Button href="/portfolio" variant="outline" size="lg" className="w-full sm:w-auto">
                  View Developer Case Studies
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wizard Footer Controls */}
        {step < 7 && (
          <div className="flex justify-between items-center pt-8 border-t border-border-muted/30 mt-8">
            <Button
              variant="outline"
              onClick={handlePrevStep}
              disabled={step === 1}
              className="text-xs py-2 px-4 flex items-center gap-1 cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            {step === 1 && (
              <Button
                variant="primary"
                onClick={handleNextStep}
                disabled={!isStep1Valid}
                className="text-xs py-2 px-5 flex items-center gap-1 cursor-pointer"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {step === 2 && (
              <Button
                variant="primary"
                onClick={handleNextStep}
                disabled={!isStep2Valid}
                className="text-xs py-2 px-5 flex items-center gap-1 cursor-pointer"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {step === 3 && (
              <Button
                variant="primary"
                onClick={handleNextStep}
                className="text-xs py-2 px-5 flex items-center gap-1 cursor-pointer"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {step === 4 && (
              <Button
                variant="primary"
                onClick={handleNextStep}
                className="text-xs py-2 px-5 flex items-center gap-1 cursor-pointer"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {step === 5 && (
              <Button
                variant="primary"
                onClick={handleNextStep}
                disabled={!isStep5Valid}
                className="text-xs py-2 px-5 flex items-center gap-1 cursor-pointer"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {step === 6 && (
              <Button
                variant="primary"
                onClick={() => {
                  trackEvent(analyticsEvents.estimatorCompleted, {
                    projectType: projectTypeId,
                    platform: platformId,
                    featuresCount: featureIds.length,
                    integrationsCount: integrationIds.length,
                    ai: aiId,
                    infra: infrastructureId,
                  });
                  handleNextStep();
                }}
                disabled={!isStep6Valid}
                className="text-xs py-2 px-5 flex items-center gap-1 cursor-pointer"
              >
                Review Estimate
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
